import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { OrderStateService } from '../../../order/services/order-state.service';
import { Order } from '../../../order/models/order';
import { BookingProductStepService } from '../../services/booking-product-step.service';
import { ProductPack } from '../../models/product-pack';
import { OrderLineType } from 'src/app/modules/order/models/order-line-type';
import { BookingOrderService } from '../../services/booking-order.service';
import { Product } from 'src/app/modules/order/models/product';
import { Counter } from '../../models/counter';
import { environment } from 'src/environments/environment';
import { NavigationService } from '../../services/navigation.services';
import { InactiveTimerService } from 'src/app/services/inactive-timer.service';
import { ProductCategory } from '../../models/product-category';
import { Category } from 'src/app/modules/order/models/category';
import { ShopSettingsService } from 'src/app/modules/general/services/shop-settings.service';
import { HeaderTitleService } from 'src/app/modules/general/services/title.service';
import { ShowBasketButtonType } from 'src/app/modules/general/models/show-basket-button-type';

@Component({
  selector: 'app-booking-product-order',
  templateUrl: './booking-product-order.component.html',
  styleUrls: ['./booking-product-order.component.css']
})
export class BookingProductOrderComponent implements OnInit, OnDestroy {

  order: Order;
  private _productPacks: ProductPack[] = [];
  private _selectedCategory: Category;


  constructor(
    private router: Router,
    private bookingProductStepService: BookingProductStepService,
    private bookingOrderService: BookingOrderService,
    private orderStateService: OrderStateService,
    private navigationService: NavigationService,
    private inactiveTimerService: InactiveTimerService,
    private shopSettingsService: ShopSettingsService,
    private headerTitleService: HeaderTitleService,
  ) { }

  ngOnInit(): void {
    this.updatePage();
    this.order = this.orderStateService.order;
    this.headerTitleService.isVisibleTitle = true;
  }

  ngOnDestroy(): void {
    this.headerTitleService.isVisibleTitle = false;
  }

  updatePage(): void {
    this.updateProductPacks(
      this.bookingProductStepService.products?.
        filter(product => this.canShowProduct(product))
    );
  }

  canShowProduct(product: Product): boolean {
    if (!this.categoryExist) {
      return true;
    }

    if (!this.productCategories.some(category => category.isLevelUp)) {
      return false;
    }

    if (this._selectedCategory && product.categoryId === this._selectedCategory.id) {
      return true;
    }

    return false;
  }

  get categoryExist(): boolean {
    return this.bookingProductStepService.categories?.length !== 0;
  }

  get showOnMainPageCategoriesIds(): number[] {
    return this.bookingProductStepService.categories?.
      filter(category => category.showOnMainPage)?.
      map(category => category.id);
  }

  get productPacks(): ProductPack[] {
    return this._productPacks;
  }

  onBack(): void {
    const productCategory = this.productCategories.find(category => category.isLevelUp);
    if (productCategory) {
      this.onCategorySelect(productCategory);
      return;
    }

    this.router.navigateByUrl('');
  }

  private updateProductPacks(products: Product[]): void {
    products = products ? products : [];
    const productPacks: ProductPack[] = [];
    const order = this.bookingOrderService.order;
    products.forEach(product => {

      const productId = product.id;
      const orderLine = order.getOrderLineByProductId(productId);
      const count = orderLine ? orderLine.quantity : 0;

      let productPack = this.findProductPackByProductVariant(product, productPacks);
      if (productPack) {
        if (count) {
          productPack.selectVariantProduct(productId, count);
        }
        return;
      }

      const countMin = product.isGroupBooking ? product.groupSizeMin : product.quantityMin;
      let countMax = product.isGroupBooking ? product.groupSizeMax : product.quantityMax;
      if (product.isStockProduct) {
        countMax = product.availableQuantity;
      }
      if (!countMax && !product.isStockProduct) {
        countMax = 1000;
      }

      productPack = new ProductPack(
        product.name,
        product.description,
        `<div class='info'">je <span class='price'>${environment.globalSettings.toMoneyStr(product.price)}</span></div>`,
        [new Counter(productId, count, '', product, countMax, countMin)],
        this.isShowProductImages ? product.getProductImageUrl() : product.iconUrl,
        product.description,
        [product],
        product.sequence
      );

      productPack.variantsProducts = this.findVariantProducts(product, products);

      productPacks.push(productPack);
    });

    this._productPacks = productPacks.sort((a, b) => a.sequence - b.sequence);
  }

  get isShowProductImages(): boolean {
    return this.shopSettingsService?.shopSettings?.isShowProductImages;
  }

  findProductPackByProductVariant(productVariant: Product, productPacks: ProductPack[]): ProductPack {
    if (!productVariant
      || !productVariant.productTemplateId
      || !productPacks) {
      return null;
    }
    return productPacks.find(productPack => productPack?.products?.some(item => item?.productTemplateId === productVariant.productTemplateId));
  }

  existProductVariant(product: Product, productPacks: ProductPack[]): boolean {
    return this.findProductPackByProductVariant(product, productPacks) != null;
  }

  findVariantProducts(product: Product, products: Product[]): Product[] {
    if (!product
      || !product.productTemplateId
      || !products) {
      return null;
    }

    const productsVariants = products.filter(item => item?.productTemplateId === product.productTemplateId);
    if (productsVariants.length <= 1) {
      return null;
    }

    return productsVariants;
  }

  onCounterChanged(counter): void {
    const product = counter.tag as Product;
    const order = this.orderStateService.order;
    const orderLine = order.getOrderLineByProductId(product.id);
    const count = counter.count;
    const groupSize = product.isGroupBooking ? count : 0;
    const qty = product.isGroupBooking && count > 0 ? 1 : count;
    const price = this.bookingOrderService.computePriceByProduct(product, qty);
    if (orderLine == null) {
      order.addOrderLine(
        OrderLineType.Product,
        product.name,
        product.id,
        price,
        product.taxRate,
        qty,
        false,
        null,
        groupSize
      );
    } else if (count > 0) {
      orderLine.quantity = qty;
      orderLine.price = price;
      orderLine.groupSize = groupSize;
    } else {
      order.deleteOrderLine(orderLine);
    }
    order.sortOrderLines();
    this.orderStateService.orderSaveToLocalStorage();
    this.inactiveTimerService.resetTimer();
  }

  get forwardError(): string {
    if (this.orderStateService.order &&
      this.orderStateService.order.orderLines &&
      this.orderStateService.order.orderLines.length) {
      return '';
    }

    return 'Please select a product!';
  }

  onProductPackUnlocked(): void {
    this.orderStateService.order.isChanged = true;
    this.inactiveTimerService.resetTimer();
  }

  onForward(): void {
    this.bookingProductStepService.forward();
  }

  get hideBack(): boolean {
    if (this.productCategories.some(category => category.isLevelUp)) {
      return false;
    }

    return this.navigationService.onlyProducts;
  }

  get displayProductCategories(): ProductCategory[] {
    return this.productCategories.filter(category => !category.isLevelUp);
  }

  get productCategories(): ProductCategory[] {
    if (!this.categoryExist) {
      return [];
    }

    let result = this.bookingProductStepService.categories?.
      filter(category => this.canShowCategory(category))?.
      map(category => new ProductCategory(category));

    if (result.some(item => item.category.showOnMainPage)) {
      this._selectedCategory = null;
      result = this.bookingProductStepService.categories?.
        filter(category => this.canShowCategory(category))?.
        map(category => new ProductCategory(category));
    }

    if (this.isShowUpButton(result.map(productCategory => productCategory.id))) {
      result = [ProductCategory.createLevelUp(this._selectedCategory.parentCategoryId, 'Up'), ...result];
    }

    return result;
  }

  isShowUpButton(categoriesIds: number[]): boolean {
    if (!this._selectedCategory) {
      return false;
    }

    if (!categoriesIds || categoriesIds.length === 0) {
      return true;
    }

    if (this.showOnMainPageCategoriesIds && this.showOnMainPageCategoriesIds.some(categoryId => categoriesIds.includes(categoryId))) {
      return false;
    }

    return true;
  }

  onCategorySelect(productCategory: ProductCategory): void {
    this._selectedCategory = this.bookingProductStepService.categories?.find(category => category?.id === productCategory?.category?.id);
    this.updatePage();
  }

  canShowCategory(category: Category, processedCategoriesIds: number[] = null): boolean {
    if (!category) {
      return false;
    }

    if (processedCategoriesIds == null) {
      if (this._selectedCategory) {
        if (this._selectedCategory.id != category.parentCategoryId) {
          return false;
        }
      } else {
        if (this.showOnMainPageCategoriesIds && !this.showOnMainPageCategoriesIds.includes(category.id)) {
          return false;
        }
      }

      processedCategoriesIds = [];
    } else if (processedCategoriesIds.includes(category.id)) {
      return false;
    }

    processedCategoriesIds.push(category.id);

    if (this.bookingProductStepService.products?.some(product => product.categoryId === category.id)) {
      return true;
    }

    if (this.bookingProductStepService.categories?.some(
      categoryItem =>
        categoryItem.parentCategoryId === category.id
        && this.canShowCategory(categoryItem, processedCategoriesIds))) {
      return true;
    }

    return false;
  }

  get basketIsReadonly(): boolean {
    return this.shopSettingsService?.shopSettings?.showBasketButtonType == ShowBasketButtonType.None;;
  }
}
